import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Stepper,
  Step,
  StepLabel,
  InputAdornment,
  IconButton,
} from "@mui/material";

import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import LockIcon from "@mui/icons-material/Lock";
import PasswordIcon from "@mui/icons-material/Password";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

const steps = ["Enter Mobile", "Verify OTP", "Reset Password"];

export default function ForgetPassword() {
  const [activeStep, setActiveStep] = useState(0);

  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Resend OTP states
  const [timer, setTimer] = useState(120);
  const [canResend, setCanResend] = useState(false);

  // ⏱ OTP TIMER
  useEffect(() => {
    if (activeStep === 1 && timer > 0) {
      const interval = setInterval(() => {
        setTimer(prev => prev - 1);
      }, 1000);

      return () => clearInterval(interval);
    }

    if (timer === 0) {
      setCanResend(true);
    }
  }, [timer, activeStep]);

  // STEP 1 – SEND OTP
  async function sendOtp(e) {
    e.preventDefault();

    const res = await fetch("https://msdskserver.onrender.com/api/user/forgetpassword-1", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ mobile }),
    });

    const data = await res.json();

    if (data.result === "Done") {
      alert("OTP sent successfully");
      setActiveStep(1);
      setTimer(120);
      setCanResend(false);
    } else {
      alert(data.message);
    }
  }

  // STEP 2 – VERIFY OTP
  async function verifyOtp(e) {
    e.preventDefault();

    const res = await fetch("https://msdskserver.onrender.com/api/user/forgetpassword-2", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ mobile, otp }),
    });

    const data = await res.json();

    if (data.result === "Done") {
      alert("OTP verified");
      setActiveStep(2);
    } else {
      alert(data.message);
    }
  }

  // 🔁 RESEND OTP
  async function resendOtp() {
    const res = await fetch("https://msdskserver.onrender.com/api/user/forgetpassword-1", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ mobile }),
    });

    const data = await res.json();

    if (data.result === "Done") {
      alert("OTP resent successfully");
      setTimer(120);
      setCanResend(false);
    } else {
      alert(data.message);
    }
  }

  // STEP 3 – RESET PASSWORD
  async function resetPassword(e) {
    e.preventDefault();

    if (password !== confirmPassword) {
      alert("Passwords do not match");
      return;
    }

    const res = await fetch("https://msdskserver.onrender.com/api/user/forgetpassword-3", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ mobile, password }),
    });

    const data = await res.json();

    if (data.result === "Done") {
      alert("Password changed successfully");
      window.location.href = "/login";
    } else {
      alert(data.message);
    }
  }

  return (
    <div className="container d-flex justify-content-center align-items-center vh-100">
      <Box sx={{ width: "100%", maxWidth: 420 }}>
        <Card sx={{ borderRadius: 3, boxShadow: 6 }}>
          <CardContent>
            <Typography variant="h5" align="center" gutterBottom>
              Forget Password
            </Typography>

            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
              {steps.map(label => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {/* STEP 1 */}
            {activeStep === 0 && (
              <form onSubmit={sendOtp}>
                <TextField
                  fullWidth
                  label="Mobile Number"
                  margin="normal"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneAndroidIcon />
                      </InputAdornment>
                    ),
                  }}
                  required
                />
                <Button fullWidth variant="contained" type="submit">
                  Send OTP
                </Button>
              </form>
            )}

            {/* STEP 2 */}
            {activeStep === 1 && (
              <>
                <form onSubmit={verifyOtp}>
                  <TextField
                    fullWidth
                    label="Enter OTP"
                    margin="normal"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon />
                        </InputAdornment>
                      ),
                    }}
                    required
                  />

                  <Button
                    fullWidth
                    variant="contained"
                    type="submit"
                    disabled={otp.length !== 6}
                  >
                    Verify OTP
                  </Button>
                </form>

                <Box sx={{ textAlign: "center", mt: 2 }}>
                  {!canResend ? (
                    <Typography variant="body2" color="text.secondary">
                      Resend OTP in{" "}
                      <strong>
                        {Math.floor(timer / 60)}:
                        {String(timer % 60).padStart(2, "0")}
                      </strong>
                    </Typography>
                  ) : (
                    <Button variant="text" onClick={resendOtp}>
                      Resend OTP
                    </Button>
                  )}
                </Box>
              </>
            )}

            {/* STEP 3 */}
            {activeStep === 2 && (
              <form onSubmit={resetPassword}>
                <TextField
                  fullWidth
                  label="New Password"
                  type={showPassword ? "text" : "password"}
                  margin="normal"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PasswordIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <VisibilityOffIcon />
                          ) : (
                            <VisibilityIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  required
                />

                <TextField
                  fullWidth
                  label="Confirm Password"
                  type={showPassword ? "text" : "password"}
                  margin="normal"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  error={confirmPassword && password !== confirmPassword}
                  helperText={
                    confirmPassword && password !== confirmPassword
                      ? "Passwords do not match"
                      : ""
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon />
                      </InputAdornment>
                    ),
                  }}
                  required
                />

                <Button
                  fullWidth
                  variant="contained"
                  color="success"
                  type="submit"
                  sx={{ mt: 2 }}
                  disabled={password !== confirmPassword || password.length < 6}
                >
                  Reset Password
                </Button>

                {password.length < 6 && (
                  <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                    Password must be at least 6 characters
                  </Typography>
                )}
              </form>
            )}
          </CardContent>
        </Card>
      </Box>
    </div>
  );
}
